import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDebounce } from "@/hooks/useHelpSearch";
import {
  ACCESS_ROLES_KEY, assignRole, fetchRoles, fetchScopes, searchPeople, SCOPE_TYPE_LABELS,
  type AccessPerson, type ScopeType,
} from "@/lib/accessControl";

const EVERYWHERE = "everywhere";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AssignRoleDialog({ open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [person, setPerson] = useState<AccessPerson | null>(null);
  const [roleId, setRoleId] = useState("");
  const [scopeType, setScopeType] = useState<string>(EVERYWHERE);
  const [scopeId, setScopeId] = useState("");
  const [endDate, setEndDate] = useState("");
  const debounced = useDebounce(query.trim(), 300);

  useEffect(() => {
    if (!open) {
      setQuery(""); setPerson(null); setRoleId(""); setScopeType(EVERYWHERE); setScopeId(""); setEndDate("");
    }
  }, [open]);

  const { data: roles } = useQuery({ queryKey: ACCESS_ROLES_KEY, queryFn: fetchRoles, enabled: open });
  const { data: scopes } = useQuery({ queryKey: ["admin", "access", "scopes"], queryFn: fetchScopes, enabled: open });
  const { data: people, isFetching: searching } = useQuery({
    queryKey: ["admin", "access", "people", debounced],
    queryFn: () => searchPeople(debounced),
    enabled: open && !person && debounced.length >= 2,
  });

  const assignable = (roles ?? []).filter((r) => r.assignable);
  const role = assignable.find((r) => r.id === roleId);
  const scoped = scopeType !== EVERYWHERE;

  const assign = useMutation({
    mutationFn: () =>
      assignRole({
        userId: person!.id,
        roleId,
        scopeType: !role?.is_system && scoped ? (scopeType as ScopeType) : null,
        scopeId: !role?.is_system && scoped ? scopeId : null,
        // End of the chosen day, in the admin's time zone.
        expiresAt: !role?.is_system && endDate ? new Date(`${endDate}T23:59:59`).toISOString() : null,
      }),
    onSuccess: () => {
      toast.success(`${role?.name} assigned to ${person?.full_name}`);
      qc.invalidateQueries({ queryKey: ["admin", "access"] });
      qc.invalidateQueries({ queryKey: ["all_user_roles"] });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSubmit = !!person && !!role && (role.is_system || !scoped || !!scopeId) && !assign.isPending;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign a role</DialogTitle>
          <DialogDescription>Give someone a role everywhere, or only in one region, program or course.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="access-person">Person</Label>
            {person ? (
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <div>
                  <div className="font-medium">{person.full_name}</div>
                  <div className="text-xs text-muted-foreground">{person.email}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setPerson(null)}>Change</Button>
              </div>
            ) : (
              <>
                <Input
                  id="access-person"
                  placeholder="Search by name or email"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoComplete="off"
                />
                {debounced.length >= 2 && (
                  <div className="max-h-48 overflow-y-auto rounded-md border">
                    {searching ? (
                      <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin" /></div>
                    ) : (people ?? []).length === 0 ? (
                      <p className="px-3 py-2 text-sm text-muted-foreground">No one found.</p>
                    ) : (
                      people!.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-muted"
                          onClick={() => setPerson(p)}
                        >
                          <span className="text-sm font-medium">{p.full_name}</span>
                          <span className="text-xs text-muted-foreground">{p.email}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger><SelectValue placeholder="Choose a role" /></SelectTrigger>
              <SelectContent>
                {assignable.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name}{r.is_system ? " (built-in)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {role?.description && <p className="text-xs text-muted-foreground">{role.description}</p>}
          </div>

          {role && !role.is_system && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Applies to</Label>
                  <Select value={scopeType} onValueChange={(v) => { setScopeType(v); setScopeId(""); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={EVERYWHERE}>Everywhere</SelectItem>
                      {(Object.keys(SCOPE_TYPE_LABELS) as ScopeType[]).map((t) => (
                        <SelectItem key={t} value={t}>One {SCOPE_TYPE_LABELS[t].toLowerCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {scoped && (
                  <div className="space-y-2">
                    <Label>{SCOPE_TYPE_LABELS[scopeType as ScopeType]}</Label>
                    <Select value={scopeId} onValueChange={setScopeId}>
                      <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent>
                        {(scopes?.[scopeType as ScopeType] ?? []).map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {scoped && (
                <p className="text-xs text-muted-foreground">
                  Permissions marked “everywhere” (like searching all regions) still apply everywhere.
                  Access-management permissions only work when the role applies everywhere.
                </p>
              )}
              <div className="space-y-2">
                <Label htmlFor="access-end">Ends on (optional)</Label>
                <Input id="access-end" type="date" min={today} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => assign.mutate()} disabled={!canSubmit}>
            {assign.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
